import React, { useState } from "react";
import styled from "styled-components";

const TrackWrapper = styled.div`
  min-height: 100vh;
  background: var(--background-light);
`;

const HeroSection = styled.section`
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    var(--background-dark) 100%
  );
  color: white;
  padding: 120px 0 80px;
  text-align: center;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  color: white;
  font-family: "Inter", sans-serif;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #e8e8e8;
  max-width: 600px;
  margin: 0 auto;
`;

const TrackingSection = styled.section`
  padding: 80px 0;
`;

const TrackingForm = styled.form`
  background: white;
  padding: 40px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--text-dark);
  }

  input {
    width: 100%;
    padding: 15px;
    border: 2px solid #e9ecef;
    border-radius: 8px;
    font-size: 1rem;

    &:focus {
      outline: none;
      border-color: var(--secondary-color);
    }
  }
`;

const TrackButton = styled.button`
  width: 100%;
  padding: 15px;
  background: var(--secondary-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #c9a660;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const InfoCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  text-align: center;

  .icon {
    width: 60px;
    height: 60px;
    background: var(--secondary-color);
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    font-size: 1.5rem;
  }

  h3 {
    color: var(--text-dark);
    margin-bottom: 15px;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
  }
`;

const TrackingResults = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 40px;
  overflow: hidden;
`;

const TrackingHeader = styled.div`
  background: var(--primary-color);
  color: white;
  padding: 30px;

  h3 {
    margin: 0 0 10px 0;
    font-size: 1.5rem;
  }

  p {
    margin: 0;
    color: #e8e8e8;
    font-size: 1rem;
  }
`;

const InfoSection = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e5e7eb;

  h4 {
    color: var(--primary-color);
    font-size: 1.1rem;
    margin-bottom: 15px;
    font-weight: 600;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
  }

  .info-item {
    p {
      margin: 3px 0;
      font-size: 0.9rem;

      strong {
        color: var(--text-dark);
        font-weight: 600;
      }
    }
  }
`;

const PackageTable = styled.div`
  padding: 25px 30px;
  border-bottom: 1px solid #e5e7eb;

  h4 {
    color: var(--primary-color);
    font-size: 1.1rem;
    margin-bottom: 15px;
    font-weight: 600;
  }

  .table-responsive {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.85rem;

    th,
    td {
      padding: 8px 12px;
      text-align: left;
      border-bottom: 1px solid #e5e7eb;
    }

    th {
      background: var(--background-light);
      font-weight: 600;
      color: var(--text-dark);
    }

    td {
      color: var(--text-light);
    }
  }

  .summary {
    margin-top: 15px;
    padding: 15px;
    background: var(--background-light);
    border-radius: 8px;

    p {
      margin: 3px 0;
      font-size: 0.9rem;
      color: var(--text-dark);

      strong {
        color: var(--primary-color);
      }
    }
  }
`;

const ShipmentDetails = styled.div`
  padding: 30px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
`;

const DetailItem = styled.div`
  h4 {
    color: var(--text-dark);
    font-size: 1rem;
    margin-bottom: 8px;
    font-weight: 600;
  }

  p {
    color: var(--text-light);
    margin: 0;
    font-size: 0.95rem;
  }

  .status {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 600;

    &.in-transit {
      background: #fef3c7;
      color: #d97706;
    }

    &.delivered {
      background: #d1fae5;
      color: #059669;
    }

    &.pending {
      background: #ddd6fe;
      color: #7c3aed;
    }
  }
`;

const TrackingTimeline = styled.div`
  padding: 30px;
  border-top: 1px solid #e5e7eb;
`;

const TimelineTitle = styled.h4`
  color: var(--text-dark);
  margin-bottom: 25px;
  font-size: 1.2rem;
`;

const TimelineItem = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  position: relative;

  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 40px;
    width: 2px;
    height: 40px;
    background: #e5e7eb;
  }

  .timeline-icon {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    flex-shrink: 0;

    &.completed {
      background: var(--secondary-color);
      color: white;
    }

    &.current {
      background: var(--primary-color);
      color: white;
    }

    &.pending {
      background: #e5e7eb;
      color: #9ca3af;
    }
  }

  .timeline-content {
    flex: 1;

    h5 {
      color: var(--text-dark);
      margin: 0 0 5px 0;
      font-size: 0.95rem;
    }

    p {
      color: var(--text-light);
      margin: 0;
      font-size: 0.85rem;
    }

    .timestamp {
      color: var(--text-light);
      font-size: 0.8rem;
      margin-top: 3px;
    }
  }
`;

const Track = () => {
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingData, setTrackingData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const statusLabels = {
    pending: "Pending",
    picked_up: "Picked up",
    on_hold: "On Hold",
    out_for_delivery: "Out for Delivery",
    in_transit: "In Transit",
    enroute: "Enroute",
    delivered: "Delivered",
    cancelled: "Cancelled",
    delayed: "Delayed",
    returned: "Returned",
  };

  const statusClasses = {
    pending: "pending",
    picked_up: "pending",
    on_hold: "pending",
    out_for_delivery: "in-transit",
    in_transit: "in-transit",
    enroute: "in-transit",
    delivered: "delivered",
    cancelled: "pending",
    delayed: "pending",
    returned: "pending",
  };

  const normalizeStatus = (status) =>
    (status || "").toString().trim().toLowerCase().replace(/\s+/g, "_");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (trackingNumber.trim()) {
      setIsLoading(true);

      try {
        const response = await fetch(
        `https://api.baypointstorage.com/api/tracking/${trackingNumber.trim()}`
         //   `http://localhost:3001/api/tracking/${trackingNumber.trim()}`
        );

        if (response.ok) {
          const apiResponse = await response.json();

          // Extract the actual data from the response
          const apiData = apiResponse.data || apiResponse;
          const normalizedStatus = normalizeStatus(apiData.status);

          // Transform API data to match our component structure
          const transformedData = {
            trackingId:
              apiData.trackingId ||
              apiData.shipmentNumber ||
              trackingNumber.trim(),
            status:
              statusLabels[normalizedStatus] || apiData.status || "Unknown",
            statusClass: statusClasses[normalizedStatus] || "pending",

            // Shipper Information
            shipper: apiData.shipper,

            // Receiver Information
            receiver: apiData.receiver,

            // Shipment Information
            containerType: apiData.containerType,
            origin: apiData.origin,
            destination: apiData.destination,
            carrier: apiData.carrier,
            shipmentType: apiData.shipmentType,
            weight: apiData.weight,
            shipmentMode: apiData.shipmentMode,
            carrierRef: apiData.carrierRef,
            product: apiData.product,
            quantity: apiData.quantity,
            paymentMode: apiData.paymentMode,
            totalFreight: apiData.totalFreight,
            estimatedDelivery: apiData.estimatedDelivery,
            departureTime: apiData.departureTime,
            pickupDate: apiData.pickupDate,
            pickupTime: apiData.pickupTime,
            comments: apiData.comments,
            currentLocation: apiData.currentLocation,

            // Package Details
            packageDetails: apiData.packageDetails,

            // Timeline - use timeline from API (not status_history)
            timeline: apiData.timeline || [],

            // Include all original API data for debugging
            rawData: apiData,
          };

          setTrackingData(transformedData);
        } else {
          setTrackingData({
            error: true,
            message:
              "Tracking number not found. Please check your tracking number and try again.",
          });
        }
      } catch (error) {
        console.error("API Error:", error);
        setTrackingData({
          error: true,
          message:
            "Unable to connect to tracking service. Please try again later.",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <TrackWrapper>
      <HeroSection>
        <Container>
          <HeroTitle>Track Your Shipment</HeroTitle>
          <HeroSubtitle>
            Enter your tracking number to see the real-time status of your
            container delivery
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <TrackingSection>
        <Container>
          <TrackingForm onSubmit={handleSubmit}>
            <FormGroup>
              <label htmlFor="trackingNumber">Tracking Number</label>
              <input
                type="text"
                id="trackingNumber"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Enter your tracking number (e.g., PC123456789)"
                required
              />
            </FormGroup>
            <TrackButton type="submit" disabled={isLoading}>
              {isLoading ? "Tracking..." : "Track Shipment"}
            </TrackButton>
          </TrackingForm>

          {trackingData && !trackingData.error && (
            <TrackingResults>
              <TrackingHeader>
                <h3 style={{ color: "#fff" }}>Shipment Details</h3>
                <div className="tracking-id">
                  Tracking ID: {trackingData.trackingId}
                </div>
              </TrackingHeader>

              {/* Basic Shipment Information - Always show if we have any data */}
              <ShipmentDetails>
                <DetailItem>
                  <h4>Tracking ID</h4>
                  <p>{trackingData.trackingId}</p>
                </DetailItem>

                {trackingData.status && (
                  <DetailItem>
                    <h4>Status</h4>
                    <p>
                      <span className={`status ${trackingData.statusClass}`}>
                        {trackingData.status}
                      </span>
                    </p>
                  </DetailItem>
                )}

                {trackingData.containerType && (
                  <DetailItem>
                    <h4>Container Type</h4>
                    <p>{trackingData.containerType}</p>
                  </DetailItem>
                )}

                {trackingData.origin && (
                  <DetailItem>
                    <h4>Origin</h4>
                    <p>{trackingData.origin}</p>
                  </DetailItem>
                )}

                {trackingData.destination && (
                  <DetailItem>
                    <h4>Destination</h4>
                    <p>{trackingData.destination}</p>
                  </DetailItem>
                )}

                {trackingData.estimatedDelivery && (
                  <DetailItem>
                    <h4>Estimated Delivery</h4>
                    <p>{trackingData.estimatedDelivery}</p>
                  </DetailItem>
                )}

                {trackingData.carrier && (
                  <DetailItem>
                    <h4>Carrier</h4>
                    <p>{trackingData.carrier}</p>
                  </DetailItem>
                )}

                {trackingData.weight && (
                  <DetailItem>
                    <h4>Weight</h4>
                    <p>{trackingData.weight}</p>
                  </DetailItem>
                )}
              </ShipmentDetails>

              {trackingData.shipper && trackingData.receiver && (
                <InfoSection>
                  <h4>Shipping Information</h4>
                  <div className="info-grid">
                    <div className="info-item">
                      <p>
                        <strong>Shipper:</strong>
                      </p>
                      <p>{trackingData.shipper.name}</p>
                      <p>{trackingData.shipper.address}</p>
                      <p>{trackingData.shipper.phone}</p>
                      <p>{trackingData.shipper.email}</p>
                    </div>
                    <div className="info-item">
                      <p>
                        <strong>Receiver:</strong>
                      </p>
                      <p>{trackingData.receiver.name}</p>
                      <p>{trackingData.receiver.address}</p>
                      <p>{trackingData.receiver.phone}</p>
                      <p>{trackingData.receiver.email}</p>
                    </div>
                  </div>
                </InfoSection>
              )}

              {trackingData.packageDetails && (
                <PackageTable>
                  <h4>Package Details</h4>
                  <div className="table-responsive">
                    <table>
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Dimensions</th>
                          <th>Weight</th>
                          <th>Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {trackingData.packageDetails.description ||
                              trackingData.product ||
                              "N/A"}
                          </td>
                          <td>
                            {trackingData.packageDetails.length &&
                            trackingData.packageDetails.width &&
                            trackingData.packageDetails.height
                              ? `${trackingData.packageDetails.length} x ${trackingData.packageDetails.width} x ${trackingData.packageDetails.height}`
                              : "N/A"}
                          </td>
                          <td>
                            {trackingData.packageDetails.weight ||
                              trackingData.weight ||
                              "N/A"}
                          </td>
                          <td>
                            {trackingData.packageDetails.qty ||
                              trackingData.quantity ||
                              "N/A"}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {(trackingData.totalFreight || trackingData.paymentMode) && (
                    <div className="summary">
                      {trackingData.totalFreight && (
                        <p>
                          <strong>Total Freight:</strong>{" "}
                          {trackingData.totalFreight}
                        </p>
                      )}
                      {trackingData.paymentMode && (
                        <p>
                          <strong>Payment Mode:</strong>{" "}
                          {trackingData.paymentMode}
                        </p>
                      )}
                    </div>
                  )}
                </PackageTable>
              )}

              <ShipmentDetails>
                {trackingData.status && (
                  <DetailItem>
                    <h4>Status</h4>
                    <p>
                      <span className={`status ${trackingData.statusClass}`}>
                        {trackingData.status}
                      </span>
                    </p>
                  </DetailItem>
                )}

                {trackingData.containerType && (
                  <DetailItem>
                    <h4>Container Type</h4>
                    <p>{trackingData.containerType}</p>
                  </DetailItem>
                )}

                {trackingData.origin && (
                  <DetailItem>
                    <h4>Origin</h4>
                    <p>{trackingData.origin}</p>
                  </DetailItem>
                )}

                {trackingData.destination && (
                  <DetailItem>
                    <h4>Destination</h4>
                    <p>{trackingData.destination}</p>
                  </DetailItem>
                )}

                {trackingData.estimatedDelivery && (
                  <DetailItem>
                    <h4>Estimated Delivery</h4>
                    <p>{trackingData.estimatedDelivery}</p>
                  </DetailItem>
                )}

                {trackingData.carrier && (
                  <DetailItem>
                    <h4>Carrier</h4>
                    <p>{trackingData.carrier}</p>
                  </DetailItem>
                )}

                {trackingData.weight && (
                  <DetailItem>
                    <h4>Weight</h4>
                    <p>{trackingData.weight}</p>
                  </DetailItem>
                )}

                {trackingData.currentLocation && (
                  <DetailItem>
                    <h4>Current Location</h4>
                    <p>{trackingData.currentLocation}</p>
                  </DetailItem>
                )}
              </ShipmentDetails>

              <TrackingTimeline>
                <TimelineTitle>Tracking Timeline</TimelineTitle>
                {trackingData.timeline && trackingData.timeline.length > 0 ? (
                  trackingData.timeline.map((item, index) => (
                    <TimelineItem key={index}>
                      <div
                        className={`timeline-icon ${
                          item.completed
                            ? "completed"
                            : item.current
                            ? "current"
                            : "pending"
                        }`}
                      >
                        {item.completed ? "✓" : index + 1}
                      </div>
                      <div className="timeline-content">
                        <h5>{item.remarks || item.status}</h5>
                        <p>{item.location}</p>
                        <div className="timestamp">
                          {item.date} at {item.time}
                        </div>
                      </div>
                    </TimelineItem>
                  ))
                ) : (
                  <p style={{ color: "var(--text-light)" }}>
                    No tracking history available
                  </p>
                )}
              </TrackingTimeline>
            </TrackingResults>
          )}

          {trackingData && trackingData.error && (
            <TrackingResults>
              <TrackingHeader style={{ background: "#ef4444" }}>
                <h3>Tracking Not Found</h3>
              </TrackingHeader>
              <div style={{ padding: "30px", textAlign: "center" }}>
                <p style={{ color: "var(--text-light)", fontSize: "1.1rem" }}>
                  {trackingData.message}
                </p>
              </div>
            </TrackingResults>
          )}

          <InfoGrid>
            <InfoCard>
              <div className="icon">
                <i className="fas fa-clock"></i>
              </div>
              <h3>Delivery Time</h3>
              <p>
                Most deliveries are completed within 3-7 business days from
                order confirmation.
              </p>
            </InfoCard>

            <InfoCard>
              <div className="icon">
                <i className="fas fa-map-marker-alt"></i>
              </div>
              <h3>Delivery Location</h3>
              <p>
                We deliver containers directly to your specified location with
                proper placement.
              </p>
            </InfoCard>

            <InfoCard>
              <div className="icon">
                <i className="fas fa-phone"></i>
              </div>
              <h3>Need Help?</h3>
              <p>
                Contact our support team at <strong>(484) 542-9847</strong> for
                assistance.
              </p>
            </InfoCard>
          </InfoGrid>
        </Container>
      </TrackingSection>
    </TrackWrapper>
  );
};

export default Track;
